<template>
  <div class="bulk-executor-editor">
    <h2 class="editor-header">{{ $t('orders.bulk_edit_executors') }}</h2>
      <div class="row">
              <div class="form-group">
                  <label class="form-label">{{ $t('orders.add_executors') }}</label>
                  <multiselect
                      v-model="selectedExecutors"
                      :options="availableExecutorOptions"
                      :multiple="true"
                      track-by="id"
                      label="name"
                      :searchable="true"
                      :close-on-select="false"
                      :placeholder="$t('orders.select_executor')"
                      :select-label="$t('orders.select_executor')"
                      :selected-label="$t('common.selected')"
                      :deselect-label="$t('common.remove_selection')"
                  >
                      <template #singleLabel="props">
                        <span class="option__desc">
                          <span class="option__title">{{ props.option.name }}</span>
                        </span>
                      </template>
                      <template #option="props">
                          <div class="option__desc">
                              <span class="option__title">{{ props.option.name }}</span>
                              <span v-if="props.option.partial" class="option__badge">{{ $t('orders.executor_partial') }}</span>
                          </div>
                      </template>
                      <template #noResult>
                          <span>{{ $t('common.not_found') }}</span>
                      </template>
                      <template #noOptions>
                          <span>{{ $t('common.no_results') }}</span>
                      </template>
                  </multiselect>
                  <div v-if="alreadyOnAllOrders.length > 0" class="already-on-all">
                      {{ $t('orders.already_on_all_orders') }}: {{ alreadyOnAllOrders.map(e => e.name).join(', ') }}
                  </div>
              </div>
              <div>
                <span class="tip-light">
                    {{ $t('orders.bulk_edit_info', { count: selectedOrderIds.length }) }}<br>
                </span>
              </div>
      </div>
    <div class="row mt-2">
      <h6 class="section-title">{{ $t('orders.selected_orders') }} ({{ selectedOrderIds.length }})</h6>
        <div
          v-for="order in selectedOrders"
          :key="order.id"
          class="px-3"
        >
            <span class="order-id">{{ order.id }} - {{ getOrderStr(order) }}</span>
            <div class="order-executors">{{ getOrderExecutors(order) }}</div>
            <hr/>
        </div>
    </div>

    <div class="modal-footer">
      <button
        type="button"
        class="btn btn-secondary form-button"
        @click="close"
      >
        {{ $t('common.cancel') }}
      </button>
      <button
        type="button"
        class="btn btn-primary form-button"
        :disabled="selectedExecutors.length < 1"
        @click="updateExecutors"
      >
        {{ $t('common.add') }}
      </button>
    </div>
  </div>
</template>

<script>
import { orderMixin } from '@/components/mixins/OrderMixin'

export default {
  name: 'BulkExecutorEditor',
  mixins: [orderMixin],
  props: {
    selectedOrderIds: {
      type: Array,
      required: true
    },
    selectedOrders: {
      type: Array,
      required: true
    },
    executorOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedExecutors: []
    }
  },
  computed: {
    alreadyOnAllOrders() {
      return this.executorOptions.filter(option =>
        this.selectedOrders.every(order =>
          order.executors && order.executors.some(e => e.company && e.company.id === option.id)
        )
      )
    },
    availableExecutorOptions() {
      const alreadyAllIds = new Set(this.alreadyOnAllOrders.map(e => e.id))
      return this.executorOptions
        .filter(option => !alreadyAllIds.has(option.id))
        .map(option => {
          const onSome = this.selectedOrders.some(order =>
            order.executors && order.executors.some(e => e.company && e.company.id === option.id)
          )
          return onSome ? { ...option, partial: true } : option
        })
    }
  },
  methods: {
    updateExecutors() {
      const data = {
        orderIds: this.selectedOrderIds,
        executors: this.selectedExecutors.map(executor => ({
          company: { id: executor.id }
        }))
      }
      this.$emit('update-executors', data)
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.bulk-executor-editor {
  width: 100%;
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  height: 100%;
}

.orders-section {
  display: flex;
  flex-direction: column;
}

.executor-section {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.section-title {
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #495057;
}

.selected-orders-list {
  flex: 1;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  padding: 0.5rem;
}

.order-id {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.order-executors {
  color: #5f8f01;
  font-size: .9em;
}

.option__badge {
  margin-left: 0.5rem;
  font-size: 0.75em;
  color: #888;
  font-style: italic;
}

.already-on-all {
  margin-top: 0.4rem;
  font-size: 0.82em;
  color: #888;
}

.form-group {
  margin-bottom: 1rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.modal-footer {
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
